import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, TrendingUp, Users } from "lucide-react";
import PageShell from "@/components/site/PageShell";
import FinalCTA from "@/components/site/FinalCTA";
import { PROJECTS } from "@/lib/projects";

export default function ProjectsPage() {
  return (
    <PageShell testId="projects-page">
      <section className="relative pt-16 pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] radial-glow opacity-40 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Portfolio &amp; Case Studies
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Real work.{" "}
              <span className="text-gradient-brand">Real numbers.</span>
            </h1>
            <p className="mt-6 text-lg text-white/65 max-w-2xl mx-auto">
              Every project below was engineered to deliver measurable growth —
              not just a pretty website. Browse the work and dive into a case
              study.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section pt-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROJECTS.map((p, i) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                data-testid={`projects-page-card-${i}`}
                className="group rounded-3xl border border-white/10 bg-white/[0.025] hover:bg-white/[0.04] hover:border-cyan-500/30 transition-all duration-500 flex flex-col overflow-hidden"
              >
                <div className="relative m-3 mb-0 rounded-2xl overflow-hidden bg-[#0a0a0a] border border-white/5">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                </div>

                <div className="flex flex-col flex-1 p-6">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-cyan-400">
                    {p.industry}
                  </div>
                  <h3 className="mt-2 font-display text-2xl font-semibold">
                    {p.name}
                  </h3>
                  <p className="mt-1.5 text-sm text-white/55">{p.services}</p>

                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-white/10 bg-white/[0.025] px-3 py-2.5">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/45 font-semibold">
                        <TrendingUp className="w-3 h-3 text-cyan-400" />
                        Traffic
                      </div>
                      <div className="mt-0.5 font-display text-lg font-bold text-gradient-brand">
                        {p.traffic}
                      </div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/[0.025] px-3 py-2.5">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/45 font-semibold">
                        <Users className="w-3 h-3 text-cyan-400" />
                        Leads
                      </div>
                      <div className="mt-0.5 font-display text-lg font-bold text-gradient-brand">
                        {p.leads}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between gap-3">
                    {p.slug === "sara-palmer-dds" ? (
                      <Link
                        to={`/projects/${p.slug}`}
                        data-testid={`project-link-${i}`}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors"
                      >
                        View Case Study <ArrowRight className="w-4 h-4" />
                      </Link>
                    ) : (
                      <span className="text-xs text-white/35">
                        Case study coming soon
                      </span>
                    )}
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`project-live-${i}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/70 hover:text-cyan-400 transition-colors"
                    >
                      Live <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </PageShell>
  );
}
